import client from "./client";

export type SupportCaseStatus =
  | "OPEN"
  | "PENDING_CUSTOMER"
  | "PENDING_INTERNAL"
  | "RESOLVED"
  | "CLOSED";

export type SupportCasePriority = "LOW" | "NORMAL" | "HIGH" | "URGENT";

export type SupportCaseCategory =
  | "BILLING"
  | "DEPLOYMENT"
  | "DOMAIN"
  | "FORMS"
  | "SEO"
  | "TRANSLATION"
  | "ACCESS"
  | "CONTENT"
  | "ACCOUNT"
  | "OTHER";

export type SupportCaseChannel =
  | "MANUAL"
  | "EMAIL"
  | "CUSTOMER_WORKSPACE"
  | "SYSTEM_ALERT"
  | "BILLING"
  | "WEBHOOK";

export type SupportLinkedResourceType =
  | "TENANT"
  | "SITE"
  | "DEPLOYMENT"
  | "DOMAIN"
  | "FORM_DEFINITION"
  | "FORM_SUBMISSION"
  | "SUBSCRIPTION"
  | "OPERATIONAL_ALERT"
  | "USER";

export interface SupportActorSummary {
  id: string;
  email: string;
}

export interface SupportCaseListItem {
  id: string;
  number: number;
  tenantId: string;
  siteId: string | null;
  requesterUserId: string | null;
  requesterEmail: string | null;
  subject: string;
  status: SupportCaseStatus;
  priority: SupportCasePriority;
  category: SupportCaseCategory;
  channel: SupportCaseChannel;
  tags: string[];
  assignedOperatorId: string | null;
  firstResponseDueAt: string | null;
  resolutionDueAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  lastActivityAt: string;
  createdAt: string;
  tenant: { id: string; slug: string; name: string };
  site: { id: string; slug: string; name: string } | null;
  requesterUser: SupportActorSummary | null;
  assignedOperator: SupportActorSummary | null;
}

export interface SupportCaseMessage {
  id: string;
  body: string;
  authorIsOperator: boolean;
  authorUserId: string;
  createdAt: string;
  author: SupportActorSummary | null;
}

export interface SupportCaseLinkedResource {
  id: string;
  resourceType: SupportLinkedResourceType;
  resourceId: string;
  label: string | null;
  createdAt: string;
}

export interface SupportCaseDetail extends SupportCaseListItem {
  updatedAt: string;
  firstResponseAt: string | null;
  messages: SupportCaseMessage[];
  linkedResources: SupportCaseLinkedResource[];
}

export interface SupportCaseListResponse {
  data: SupportCaseListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface ListSupportCasesParams {
  status?: SupportCaseStatus[];
  siteId?: string;
  q?: string;
  page?: number;
  limit?: number;
}

export interface CreateSupportCasePayload {
  siteId?: string;
  subject: string;
  message: string;
  category?: SupportCaseCategory;
}

function buildListParams(params: ListSupportCasesParams = {}): string {
  const searchParams = new URLSearchParams();
  params.status?.forEach((status) => searchParams.append("status", status));
  if (params.siteId) searchParams.set("siteId", params.siteId);
  if (params.q) searchParams.set("q", params.q);
  if (params.page) searchParams.set("page", String(params.page));
  if (params.limit) searchParams.set("limit", String(params.limit));
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export async function getSupportCases(
  tenantId: string,
  params: ListSupportCasesParams = {},
): Promise<SupportCaseListResponse> {
  const { data } = await client.get<SupportCaseListResponse>(
    `/tenants/${tenantId}/support-cases${buildListParams(params)}`,
  );
  return data;
}

export async function getSupportCase(
  tenantId: string,
  caseId: string,
): Promise<SupportCaseDetail> {
  const { data } = await client.get<SupportCaseDetail>(
    `/tenants/${tenantId}/support-cases/${caseId}`,
  );
  return data;
}

export async function createSupportCase(
  tenantId: string,
  payload: CreateSupportCasePayload,
): Promise<SupportCaseDetail> {
  const { data } = await client.post<SupportCaseDetail>(
    `/tenants/${tenantId}/support-cases`,
    payload,
  );
  return data;
}

export async function replySupportCase(
  tenantId: string,
  caseId: string,
  payload: { body: string },
): Promise<SupportCaseDetail> {
  const { data } = await client.post<SupportCaseDetail>(
    `/tenants/${tenantId}/support-cases/${caseId}/messages`,
    payload,
  );
  return data;
}
